import { createContext, useContext, useMemo, useState } from "react";

import Service from "../../api/service";
import { useCompanyContext } from "../CompanyContext";
import { useAdminContext } from "./AdminContext";

const AdminsContext = createContext(null);

export const useAdminsContext = () => {
  const v = useContext(AdminsContext);
  if (!v) {
    throw new Error("useAdminsContext must be used inside AdminsProvider");
  }
  return v;
};

export const AdminsProvider = ({ children }) => {
  const [admins, setAdmins] = useState(null);
  const [count, setCount] = useState(0);
  const { sysAdmin } = useAdminContext();
  const { company } = useCompanyContext();

  const getAll = async (page = 1, pageSize = 50) => {
    const response = await Service.get(
      `/api/v1/auth/admin/admins/?page=${page}&pageSize=${pageSize}`,
    );
    const payload = response?.data;
    if (payload) {
      setAdmins(payload.data);
      setCount(payload.count);
    }
    return response;
  };

  const getAllByAdminCompanyId = async (page = 1, pageSize = 50) => {
    const response = await Service.get(
      `/api/v1/company/admins/?page=${page}&pageSize=${pageSize}`,
    );
    const payload = response?.data;
    if (payload) {
      setAdmins(payload.data);
      setCount(payload.count);
    }
    return response;
  };

  // Build the `&fetch_by_admin[]=X&fetch_by_admin[]=Y…` query fragment used by
  // filter-by-admin endpoints. Shape depends on the caller's role.
  const parseQueryArray = () => {
    const prep = "&fetch_by_admin[]=";
    const role = String(sysAdmin?.role);
    const currentAdminId = sysAdmin?.admin_id;
    const users = company?.Admins;

    if (["company_owner", "system_admin"].includes(role) && users) {
      return users.map(({ admin_id }) => `${prep}${admin_id}`).join("");
    }
    if (users && role === "content_admin") {
      return users
        .filter((x) => x?.role === "content_creator")
        .map((x) => `${prep}${x?.admin_id}`)
        .join("");
    }
    return currentAdminId ? `${prep}${currentAdminId}` : "";
  };

  const create = async (data) =>
    Service.post("/api/v1/auth/admin/signup", data);

  const updateProfile = async (admin_id, data) =>
    Service.put(`/api/v1/auth/admin/update/profile/${admin_id}`, data);

  const updatePassword = async (admin_id, data) =>
    Service.put(`/api/v1/auth/admin/update/password/${admin_id}`, data);

  const deleteAdmin = async (admin_id) =>
    Service.delete(`/api/v1/auth/admin/delete/${admin_id}`);

  const value = useMemo(
    () => ({
      admins,
      count,
      setAdmins,
      getAll,
      getAllByAdminCompanyId,
      create,
      updateProfile,
      updatePassword,
      deleteAdmin,
      parseQueryArray,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [admins, count, sysAdmin?.role, sysAdmin?.admin_id, company?.Admins],
  );

  return (
    <AdminsContext.Provider value={value}>{children}</AdminsContext.Provider>
  );
};
